import { getApiUrl } from '../config/api';

export interface VeoCreditUserData {
  userId: string;
  email: string;
  plan: string;
  role: string;
  credits: number;
  totalPurchased: number;
  totalConsumed: number;
  totalSpent: number;
  hasVeoCredits?: boolean; // NEW: Indicates if user has ever purchased VEO credits
}

export interface VeoCreditBalance {
  id: string;
  userId: string;
  credits: number;
  totalPurchased: number;
  totalConsumed: number;
  totalSpent: number;
  createdAt: string;
  updatedAt: string;
}

export interface VeoCreditTransaction {
  id: string;
  userId: string;
  type: 'purchase' | 'consumption' | 'refund' | 'admin_adjustment';
  credits: number;
  amount?: number;
  stripePaymentIntentId?: string;
  description: string;
  veoModel?: string;
  videoProjectId?: string;
  balanceAfter: number;
  createdAt: string;
  updatedAt: string;
}

export interface VeoSystemOverview {
  overview: {
    totalUsers: number;
    totalUsersInSystem?: number; // NEW: Total users in system
    usersWithoutVeoCredits?: number; // NEW: Users without VEO credits
    totalCreditsInSystem: number;
    totalCreditsPurchased: number;
    totalCreditsConsumed: number;
    totalRevenue: number;
    averageCreditsPerUser: number;
  };
  recentActivity: {
    [key: string]: {
      count: number;
      totalCredits: number;
      totalAmount: number;
    };
  };
  topUsers: Array<{
    userId: string;
    email: string;
    credits: number;
    totalConsumed: number;
    totalSpent: number;
  }>;
  generatedAt: string;
}

export interface VeoCreditUserDetails {
  userId: string;
  user: {
    id: string;
    email: string;
    plan: string;
    role: string;
  };
  balance: VeoCreditBalance;
  transactions: VeoCreditTransaction[];
  hasVeoCredits: boolean;
}

class VeoCreditAdminService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  /**
   * Test VEO Credits admin endpoint connectivity
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(getApiUrl('/admin/veo-credits/test'), {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Test failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('VEO Credits admin test failed:', error);
      return {
        success: false,
        message: error.message || 'Connection test failed'
      };
    }
  }

  /**
   * Search users who have VEO credits (now includes ALL users for credit assignment)
   */
  async searchUsersWithVeoCredits(query: string = '', limit: number = 20): Promise<{
    users: VeoCreditUserData[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    params.append('limit', limit.toString());

    const response = await fetch(
      getApiUrl(`/admin/veo-credits/users/search?${params.toString()}`),
      {
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      if (response.status === 500) {
        // Server error - might be because no users exist yet
        console.warn('Server error fetching users - might be empty database');
        return { users: [], total: 0 };
      }
      throw new Error(`Failed to search users: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get detailed VEO credit information for a specific user
   */
  async getUserVeoCredits(userId: string): Promise<VeoCreditUserDetails> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/veo-credits`), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user VEO credits');
    }

    return await response.json();
  }

  /**
   * Adjust VEO credits for a user (can be positive or negative)
   */
  async adjustUserVeoCredits(
    userId: string,
    credits: number,
    reason: string
  ): Promise<{
    success: boolean;
    newBalance: number;
    transaction: VeoCreditTransaction;
    message: string;
  }> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/veo-credits/adjust`), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ credits, reason }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to adjust VEO credits');
    }

    return await response.json();
  }

  /**
   * Grant VEO credits to a user (always positive)
   */
  async grantUserVeoCredits(
    userId: string,
    credits: number,
    reason: string
  ): Promise<{
    success: boolean;
    newBalance: number;
    transactionId: string;
    message: string;
  }> {
    const response = await fetch(getApiUrl(`/admin/users/${userId}/veo-credits/grant`), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ credits, reason }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to grant VEO credits');
    }

    return await response.json();
  }

  /**
   * Get system-wide VEO credits overview and analytics
   */
  async getVeoSystemOverview(): Promise<VeoSystemOverview> {
    const response = await fetch(getApiUrl('/admin/veo-credits/overview'), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 500) {
        // Server error - might be because no VEO credit data exists yet
        console.warn('Server error fetching VEO overview - returning empty data');
        return {
          overview: {
            totalUsers: 0,
            totalCreditsInSystem: 0,
            totalCreditsPurchased: 0,
            totalCreditsConsumed: 0,
            totalRevenue: 0,
            averageCreditsPerUser: 0,
          },
          recentActivity: {},
          topUsers: [],
          generatedAt: new Date().toISOString(),
        };
      }
      throw new Error(`Failed to fetch VEO credits overview: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Format credits number for display
   */
  formatCredits(credits: number): string {
    return credits.toLocaleString();
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Get transaction type display name
   */
  getTransactionTypeDisplay(type: string): { label: string; icon: string; color: string } {
    switch (type) {
      case 'purchase':
        return { label: 'Purchase', icon: '💳', color: '#10b981' };
      case 'consumption':
        return { label: 'Video Generation', icon: '🎬', color: '#06b6d4' };
      case 'admin_adjustment':
        return { label: 'Admin Adjustment', icon: '⚙️', color: '#8b5cf6' };
      case 'refund':
        return { label: 'Refund', icon: '↩️', color: '#f59e0b' };
      default:
        return { label: type, icon: '📝', color: '#64748b' };
    }
  }

  /**
   * Calculate user credit utilization rate
   */
  calculateUtilizationRate(totalPurchased: number, credits: number): number {
    if (totalPurchased === 0) return 0;
    const consumed = totalPurchased - credits;
    return Math.round((consumed / totalPurchased) * 100);
  }
}

export const veoCreditAdminService = new VeoCreditAdminService();
